import { Suspense } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  RouterProvider,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import { UserStatus } from "./api/SolarCloudApi";
import { Messages } from "./components/Messages";
import { useNewOrder } from "./queries/order/useNewOrder";
import {
  useOrderUuid,
  useHasOrderUuidHydrated,
  useOrderUuidAge,
} from "./stores/orderIdStore";
import CalculatorPage from "./pages/CalculatorPage";
import LoadingPage from "./pages/LoadingPage";
// import { logger } from "./util/logger";
import LoginPage from "./pages/LoginPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ContactPage from "./pages/ContactPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import BuyPanelsPage from "@/pages/BuyPanelsPage";
import BuyPanelsActiveRedirect from "./components/routing/BuyPanelsActiveRedirect";
import BuySolarRouteGuard from "./components/routing/BuySolarRouteGuard";
import { useInitialEnvironmentContext } from "@/contexts/EnvironmentContext";
import ProfileContextProvider, {
  useProfileContext,
} from "./contexts/ProfileContext";
import DashboardPage from "./pages/DashboardPage";
import { ProtectedRoute } from "./components/routing/ProtectedRoute";
import { useAuth } from "@/hooks/useAuth";
import { Toaster } from "./components/ui/Sonner";
import UserErrorPage from "./pages/UserErrorPage";
import VerificationPage from "./pages/VerificationPage";
import MetaPixelContextProvider from "./contexts/MetaPixelContext";
import { AuthHubListener } from "./lib/authHubListener";
import { usePageView } from "./hooks/usePageView";

// const BuyPanelsPage = React.lazy(() => import("./pages/BuyPanelsPage"));
// const ChangePasswordPage = React.lazy(() => import("./pages/ChangePasswordPage"));
// const ContactPage = React.lazy(() => import("./pages/ContactPage"));
// const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
// const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
// const LoginPage = React.lazy(() => import("./pages/LoginPage"));
// const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
// const SignupPage = React.lazy(() => import("./pages/SignupPage"));
// const TermsAndConditionsPage = React.lazy(() => import("./pages/TrackerPage"));
// const AllocationsPanel = React.lazy(() => import("./panel/AllocationsPanel"));
// const ChangePasswordPanel = React.lazy(() => import("./panel/ChangePasswordPanel"));
// const ContactPanel = React.lazy(() => import("./panel/ContactPanel"));
// const DashboardPanel = React.lazy(() => import("./panel/DashboardPanel"));
// const InvoicesPanel = React.lazy(() => import("./panel/InvoicesPanel"));
// const LocationsPanel = React.lazy(() => import("./panel/LocationsPanel"));
// const TermsAndConditionsPanel = React.lazy(() => import("./panel/TermsAndConditionsPanel"));
// const TrackerPanel = React.lazy(() => import("./panel/TrackerPanel"));

type NavigateWithTraceProps = {
  forced?: boolean;
  to?: string;
};

const NavigateWithTrace = ({ to, forced }: NavigateWithTraceProps) => {
  // const { data: user, isLoading, isFetching } = useUser();
  const { authentication, isAuthenticationLoading } = useProfileContext();
  const orderUuid = useOrderUuid();
  const { data: newOrder, isLoading: isNewOrderLoading } = useNewOrder({
    uuid: orderUuid,
  });
  const orderAge = useOrderUuidAge();

  const defaultPath: string =
    to ||
    (() => {
      if (
        newOrder?.status === "Draft" &&
        orderAge &&
        orderAge < 1000 * 60 * 10
      ) {
        if (authentication?.loggedIn) {
          return `/buypanels/ownership`;
        }
        return `/buypanels`;
      }
      if (authentication?.loggedIn) {
        {
          switch (authentication?.user.status) {
            default:
            case UserStatus.ACTIVE:
            case UserStatus.PENDING:
            case UserStatus.PROFILED:
            case UserStatus.VERIFIED:
              return "/dashboard/";

            case UserStatus.UNVERIFIED:
              return "/buypanels";

            case UserStatus.CANCELLED:
            case UserStatus.COMPROMISED:
            case UserStatus.INACTIVE:
              return "/signup/error";
          }
        }
      } else {
        return "/login";
      }
    })();

  // Preserve query parameters
  const search = location.search;
  const target = search ? `${defaultPath}${search}` : defaultPath;

  // logger.log(`@@@@ Navigating to ${target}${forced ? ` forced from ${location.pathname}` : ""}`);
  if (isAuthenticationLoading || isNewOrderLoading) {
    return <LoadingPage />;
  }

  return <Navigate to={target} />;
};

// enum AppState {
//   LoggedOut = "LoggedOut",
//   Signup = "Signup",
//   Verify = "Verify",
//   Profile = "Profile",
//   Buying = "Buying",
//   Payment = "Payment",
//   LoggedIn = "LoggedIn",
// }

export default function App() {
  const environment = useInitialEnvironmentContext();
  const hasHydrated = useHasOrderUuidHydrated();

  if (!environment || !hasHydrated) {
    return <LoadingPage />;
  }
  return (
    <ProfileContextProvider>
      <MetaPixelContextProvider>
        <AppWithEnvironment />
      </MetaPixelContextProvider>
    </ProfileContextProvider>
  );
}

const RoutesWithTracking = () => {
  usePageView();
  
  const Logout = () => {
    const navigate = useNavigate();
    const { handleSignOut } = useAuth();

    try {
      handleSignOut().then(() => {
        navigate("/");
      });
    } catch (error) {
      console.log(`Failed to signOut`, error);
    }

    return <></>;
  };

  return (
    <Routes>
      <Route key="11" path="/" element={<NavigateWithTrace forced />} />
      ,
      <Route key="30" path="/login" element={<LoginPage />} />
      <Route
        key="33"
        path="/forgot-password"
        element={<ForgotPasswordPage />}
      />
      <Route
        key="34"
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        key="35"
        path="/changepassword"
        element={<ChangePasswordPage />}
      />
      <Route key="36" path="/verify" element={<VerificationPage />} />
      <Route
        path="/buypanels/*"
        element={
          <BuyPanelsActiveRedirect>
            <BuyPanelsPage />
          </BuyPanelsActiveRedirect>
        }
      />
      <Route
        path="/buysolar/*"
        element={
          <BuySolarRouteGuard>
            <BuyPanelsPage />
          </BuySolarRouteGuard>
        }
      />
      <Route path="/virtualbatteryincluded/*" element={<BuyPanelsPage />} />
      <Route path="/evsolar/*" element={<BuyPanelsPage />} />
      <Route path="/corporatesolar/*" element={<BuyPanelsPage />} />
      <Route
        path="/buypanels-v2/*"
        element={<NavigateWithTrace to={`/buypanels`} />}
      />
      <Route
        path="/buypanels2"
        element={<NavigateWithTrace to={`/buypanels`} />}
      />
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route key="92" path="/contact" element={<ContactPage />} />
      <Route key="93" path="/logout" element={<Logout />} />
      <Route key="94" path="/logoff" element={<Logout />} />
      <Route
        key="95"
        path="/auth/redirect"
        element={<NavigateWithTrace forced />}
      />
      <Route
        key="95"
        path="/auth/signout"
        element={<NavigateWithTrace to={"/login"} forced />}
      />
      <Route key="96" path="*" element={<NavigateWithTrace to={`/`} />} />
      <Route key="97" path="/signup/error" element={<UserErrorPage />} />
      <Route key="loading" path="/loading" element={<LoadingPage />} />
      <Route
        key="calculator"
        path="/calculator"
        element={<CalculatorPage />}
      />
    </Routes>
  );
}

const AppWithEnvironment = () => {
  const Logout = () => {
    const navigate = useNavigate();
    const { handleSignOut } = useAuth();

    try {
      handleSignOut().then(() => {
        navigate("/");
      });
    } catch (error) {
      console.log(`Failed to signOut`, error);
    }

    return <></>;
  };

  return (
    <Suspense fallback={<LoadingPage />}>
      <Router basename="/">
        <AuthHubListener />
        <Messages />
        <Toaster />
        <RoutesWithTracking />
      </Router>
    </Suspense>
  );
};
